import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, User, Bell, Info, Bot, Save, Loader2, AlertCircle, CheckCircle, Users, Calendar, CalendarCheck, Clock, Plus, Trash2, Edit2, X, Camera, Upload, Palette, Sparkles, ArrowRight } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import { storeApi, promptsApi, botConfigApi, workersApi, schedulesApi, appointmentTypesApi, profileApi } from '../services/api';
import BotSettingsModal from '../components/settings/BotSettingsModal';
import './SettingsPage.css';

const SettingsPage = () => {
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage, availableLanguages, isRTL } = useLanguage();
  const { user, updateUser } = useAuth();
  const { theme, changeTheme } = useTheme();

  // Profile picture state
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [pictureError, setPictureError] = useState(null);
  const [pictureSuccess, setPictureSuccess] = useState(false);
  const fileInputRef = useRef(null);

  // Store info state
  const [storeInfo, setStoreInfo] = useState(null);
  const [loadingStoreInfo, setLoadingStoreInfo] = useState(true);

  // Bot mode / on-off state (self-serve AI vs manual, and active toggle)
  const [botMode, setBotMode] = useState({ auto_create: false, bot_enabled: true });
  const [savingBotMode, setSavingBotMode] = useState(false);
  const [botModeSaveStatus, setBotModeSaveStatus] = useState(null);
  
  // Prompts state
  const [prompts, setPrompts] = useState({
    business_name: '',
    business_type: '',
    identity_prompt: '',
    custom_rules: '',
    opening_message: '',
  });
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [savingPrompts, setSavingPrompts] = useState(false);
  const [promptsSaveStatus, setPromptsSaveStatus] = useState(null);
  
  // Bot config state
  const [botConfig, setBotConfig] = useState({
    language: 'ar',
    max_future_appointments: 2,
    lead_summary_enabled: false,
    lead_summary_language: 'ar',
    ai_appointments_enabled: false,
    ai_products_enabled: false,
  });
  const [savingBotConfig, setSavingBotConfig] = useState(false);
  const [botConfigSaveStatus, setBotConfigSaveStatus] = useState(null);

  // Bot Settings modal
  const [showBotSettings, setShowBotSettings] = useState(false);

  // Workers state
  const [workers, setWorkers] = useState([]);
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  const [showWorkerModal, setShowWorkerModal] = useState(false);
  const [editingWorker, setEditingWorker] = useState(null);
  const [workerForm, setWorkerForm] = useState({ name: '' });

  // Schedules state
  const [schedules, setSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({
    day_of_week: 0,
    start_time: '09:00',
    end_time: '17:00',
    worker_id: null
  });

  // Appointment Types state
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeForm, setTypeForm] = useState({
    name: '',
    duration_minutes: 30,
    price: '',
    description: '',
    prompt_notes: ''
  });

  const dayOptions = [
    { value: 0, label: t('days.monday') },
    { value: 1, label: t('days.tuesday') },
    { value: 2, label: t('days.wednesday') },
    { value: 3, label: t('days.thursday') },
    { value: 4, label: t('days.friday') },
    { value: 5, label: t('days.saturday') },
    { value: 6, label: t('days.sunday') },
  ];

  // Fetch store info on mount
  useEffect(() => {
    const fetchStoreInfo = async () => {
      try {
        const response = await storeApi.getInfo();
        if (response.success) {
          setStoreInfo(response.data);
          setBotMode({
            auto_create: !!response.data.auto_create,
            bot_enabled: response.data.bot_enabled !== false,
          });
        }
      } catch (error) {
        console.error('Failed to fetch store info:', error);
      } finally {
        setLoadingStoreInfo(false);
      }
    };
    fetchStoreInfo();
  }, []);

  // Fetch data when store has auto_create=true
  useEffect(() => {
    if (storeInfo?.auto_create) {
      fetchPrompts();
      fetchBotConfig();
      fetchWorkers();
      fetchSchedules();
      fetchAppointmentTypes();
    }
  }, [storeInfo?.auto_create]);

  const fetchPrompts = async () => {
    setLoadingPrompts(true);
    try {
      const response = await promptsApi.get();
      if (response.success && response.data) {
        setPrompts({
          business_name: response.data.business_name || '',
          business_type: response.data.business_type || '',
          identity_prompt: response.data.identity_prompt || '',
          custom_rules: response.data.custom_rules || '',
          opening_message: response.data.opening_message || '',
        });
      }
    } catch (error) {
      console.error('Failed to fetch prompts:', error);
    } finally {
      setLoadingPrompts(false);
    }
  };

  const fetchBotConfig = async () => {
    try {
      const response = await botConfigApi.get();
      if (response.success && response.data) {
        setBotConfig({
          language: response.data.language || 'ar',
          max_future_appointments: response.data.max_future_appointments || 2,
          lead_summary_enabled: !!response.data.lead_summary_enabled,
          lead_summary_language: response.data.lead_summary_language || 'ar',
          ai_appointments_enabled: !!response.data.ai_appointments_enabled,
          ai_products_enabled: !!response.data.ai_products_enabled,
        });
      }
    } catch (error) {
      console.error('Failed to fetch bot config:', error);
    }
  };

  const fetchWorkers = async () => {
    setLoadingWorkers(true);
    try {
      const response = await workersApi.list();
      if (response.success) {
        setWorkers(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch workers:', error);
    } finally {
      setLoadingWorkers(false);
    }
  };

  const fetchSchedules = async () => {
    setLoadingSchedules(true);
    try {
      const response = await schedulesApi.list();
      if (response.success) {
        setSchedules(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
    } finally {
      setLoadingSchedules(false);
    }
  };

  const fetchAppointmentTypes = async () => {
    setLoadingTypes(true);
    try {
      const response = await appointmentTypesApi.list();
      if (response.success) {
        setAppointmentTypes(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch appointment types:', error);
    } finally {
      setLoadingTypes(false);
    }
  };

  const handlePromptChange = (field, value) => {
    setPrompts(prev => ({ ...prev, [field]: value }));
    setPromptsSaveStatus(null);
  };

  const handleBotConfigChange = (field, value) => {
    setBotConfig(prev => ({ ...prev, [field]: value }));
    setBotConfigSaveStatus(null);
  };

  const savePrompts = async () => {
    setSavingPrompts(true);
    setPromptsSaveStatus(null);
    try {
      const response = await promptsApi.update(prompts);
      if (response.success) {
        setPromptsSaveStatus('success');
        setTimeout(() => setPromptsSaveStatus(null), 3000);
        return true;
      }
      setPromptsSaveStatus('error');
      return false;
    } catch (error) {
      console.error('Failed to save prompts:', error);
      setPromptsSaveStatus('error');
      return false;
    } finally {
      setSavingPrompts(false);
    }
  };

  const saveBotConfig = async () => {
    setSavingBotConfig(true);
    setBotConfigSaveStatus(null);
    try {
      const response = await botConfigApi.update(botConfig);
      if (response.success) {
        setBotConfigSaveStatus('success');
        setTimeout(() => setBotConfigSaveStatus(null), 3000);
        return true;
      }
      setBotConfigSaveStatus('error');
      return false;
    } catch (error) {
      console.error('Failed to save bot config:', error);
      setBotConfigSaveStatus('error');
      return false;
    } finally {
      setSavingBotConfig(false);
    }
  };

  const saveBotMode = async () => {
    setSavingBotMode(true);
    setBotModeSaveStatus(null);
    try {
      const response = await botConfigApi.update({
        auto_create: botMode.auto_create,
        bot_enabled: botMode.bot_enabled,
      });
      if (response.success) {
        setBotModeSaveStatus('success');
        // Reflect the new mode immediately so the AI editors show/hide.
        setStoreInfo((prev) =>
          prev ? { ...prev, auto_create: botMode.auto_create, bot_enabled: botMode.bot_enabled } : prev
        );
        setTimeout(() => setBotModeSaveStatus(null), 3000);
      } else {
        setBotModeSaveStatus('error');
      }
    } catch (error) {
      console.error('Failed to save bot mode:', error);
      setBotModeSaveStatus('error');
    } finally {
      setSavingBotMode(false);
    }
  };

  // Worker handlers
  const openWorkerModal = (worker = null) => {
    if (worker) {
      setEditingWorker(worker);
      setWorkerForm({ name: worker.name });
    } else {
      setEditingWorker(null);
      setWorkerForm({ name: '' });
    }
    setShowWorkerModal(true);
  };

  const saveWorker = async () => {
    if (!workerForm.name.trim()) return;
    try {
      if (editingWorker) {
        await workersApi.update(editingWorker.id, { name: workerForm.name });
      } else {
        await workersApi.create(workerForm.name);
      }
      fetchWorkers();
      setShowWorkerModal(false);
    } catch (error) {
      console.error('Failed to save worker:', error);
    }
  };

  const deleteWorker = async (id) => {
    if (!window.confirm(t('settings.confirmDeleteWorker'))) return;
    try {
      await workersApi.delete(id);
      fetchWorkers();
    } catch (error) {
      console.error('Failed to delete worker:', error);
    }
  };

  // Schedule handlers
  const openScheduleModal = (schedule = null) => {
    if (schedule) {
      setEditingSchedule(schedule);
      setScheduleForm({
        day_of_week: schedule.day_of_week,
        start_time: schedule.start_time,
        end_time: schedule.end_time,
        worker_id: schedule.worker_id
      });
    } else {
      setEditingSchedule(null);
      setScheduleForm({
        day_of_week: 0,
        start_time: '09:00',
        end_time: '17:00',
        worker_id: null
      });
    }
    setShowScheduleModal(true);
  };

  const saveSchedule = async () => {
    try {
      if (editingSchedule) {
        await schedulesApi.update(editingSchedule.id, scheduleForm);
      } else {
        await schedulesApi.create(scheduleForm);
      }
      fetchSchedules();
      setShowScheduleModal(false);
    } catch (error) {
      console.error('Failed to save schedule:', error);
    }
  };

  const deleteSchedule = async (id) => {
    if (!window.confirm(t('settings.confirmDeleteSchedule'))) return;
    try {
      await schedulesApi.delete(id);
      fetchSchedules();
    } catch (error) {
      console.error('Failed to delete schedule:', error);
    }
  };

  // Appointment Type handlers
  const openTypeModal = (type = null) => {
    if (type) {
      setEditingType(type);
      setTypeForm({
        name: type.name,
        duration_minutes: type.duration_minutes,
        price: type.price || '',
        description: type.description || '',
        prompt_notes: type.prompt_notes || ''
      });
    } else {
      setEditingType(null);
      setTypeForm({
        name: '',
        duration_minutes: 30,
        price: '',
        description: '',
        prompt_notes: ''
      });
    }
    setShowTypeModal(true);
  };

  const saveType = async () => {
    if (!typeForm.name.trim()) return;
    try {
      if (editingType) {
        await appointmentTypesApi.update(editingType.id, typeForm);
      } else {
        await appointmentTypesApi.create(typeForm);
      }
      fetchAppointmentTypes();
      setShowTypeModal(false);
    } catch (error) {
      console.error('Failed to save appointment type:', error);
    }
  };

  const deleteType = async (id) => {
    if (!window.confirm(t('settings.confirmDeleteType'))) return;
    try {
      await appointmentTypesApi.delete(id);
      fetchAppointmentTypes();
    } catch (error) {
      console.error('Failed to delete appointment type:', error);
    }
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setPictureError(t('settings.profilePicInvalidType'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setPictureError(t('settings.profilePicTooLarge'));
      return;
    }

    setUploadingPicture(true);
    setPictureError(null);
    setPictureSuccess(false);

    try {
      const response = await profileApi.uploadPicture(file);
      if (response.success) {
        updateUser({ profile_picture_url: response.data.profile_picture_url });
        setPictureSuccess(true);
        setTimeout(() => setPictureSuccess(false), 3000);
      } else {
        setPictureError(response.error || t('settings.profilePicUploadFailed'));
      }
    } catch (error) {
      console.error('Failed to upload profile picture:', error);
      setPictureError(error.message || t('settings.profilePicUploadFailed'));
    } finally {
      setUploadingPicture(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleLanguageChange = (e) => {
    changeLanguage(e.target.value);
  };

  const botLanguageOptions = [
    { value: 'ar', label: 'العربية (Arabic)' },
    { value: 'he', label: 'עברית (Hebrew)' },
    { value: 'en', label: 'English' },
  ];

  const isAutoCreate = storeInfo?.auto_create === true;

  return (
    <div className={`settings-page ${isRTL ? 'rtl' : ''}`}>
      {/* Page Header */}
      <div className="page-header">
        <div className="page-title-section">
          <h1 className="page-title">{t('settings.title')}</h1>
          <p className="page-subtitle">{t('settings.subtitle')}</p>
        </div>
      </div>

      {/* Settings Content */}
      <div className="settings-content">
        {/* Bot Mode + On/Off — always visible for chakra stores so you can switch
            between the self-serve AI bot and manual handling, and turn it off. */}
        {!loadingStoreInfo && (storeInfo?.api_type || 'chakra') === 'chakra' && (
          <section className="settings-section card span-full">
            <div className="section-header">
              <Bot size={20} className="section-icon" />
              <h2 className="section-title">{t('settings.botModeTitle', 'Bot')}</h2>
            </div>
            <div className="section-content">
              <div className="setting-item">
                <div className="setting-info">
                  <h4>{t('settings.botMode', 'Bot mode')}</h4>
                  <p>{t('settings.botModeHint', 'Self-serve runs the AI bot you edit below. Manual means you handle this store yourself.')}</p>
                </div>
                <select
                  className="input setting-select"
                  value={botMode.auto_create ? 'self' : 'manual'}
                  onChange={(e) => setBotMode((m) => ({ ...m, auto_create: e.target.value === 'self' }))}
                >
                  <option value="self">{t('settings.modeSelfServe', 'Self-serve (AI)')}</option>
                  <option value="manual">{t('settings.modeManual', 'Manual')}</option>
                </select>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <h4>{t('settings.botActive', 'Bot active')}</h4>
                  <p>{t('settings.botActiveHint', 'Turn the AI auto-replies on or off. Incoming chats are still recorded when off.')}</p>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={botMode.bot_enabled}
                    disabled={!botMode.auto_create}
                    onChange={(e) => setBotMode((m) => ({ ...m, bot_enabled: e.target.checked }))}
                  />
                  <span className="switch-slider"></span>
                </label>
              </div>

              <div className="save-section">
                <button
                  className={`btn btn-primary save-btn ${savingBotMode ? 'loading' : ''}`}
                  onClick={saveBotMode}
                  disabled={savingBotMode}
                >
                  {savingBotMode ? (
                    <>
                      <Loader2 className="spinner" size={18} />
                      {t('settings.saving')}
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      {t('common.save')}
                    </>
                  )}
                </button>
                <button
                  className={`btn ${isAutoCreate ? 'btn-primary bot-launcher-btn' : 'btn-secondary bot-launcher-btn'}`}
                  onClick={() => setShowBotSettings(true)}
                  disabled={!isAutoCreate}
                  title={!isAutoCreate ? t('botSettings.selfServeOnly', 'Switch the bot to Self-serve (AI) to configure it') : undefined}
                >
                  <Sparkles size={18} />
                  {t('botSettings.open', 'Open Bot Settings')}
                </button>
                {botModeSaveStatus === 'success' && (
                  <span className="save-status success">
                    <CheckCircle size={16} />
                    {t('settings.savedSuccessfully')}
                  </span>
                )}
                {botModeSaveStatus === 'error' && (
                  <span className="save-status error">
                    <AlertCircle size={16} />
                    {t('settings.failedToSave')}
                  </span>
                )}
              </div>
            </div>
          </section>
        )}

        {!loadingStoreInfo && (
          <section className="settings-section card span-full">
            <div className="section-header">
              <CalendarCheck size={20} className="section-icon" />
              <h2 className="section-title">{t('settings.externalBooking', 'External appointment link')}</h2>
            </div>
            <div className="section-content">
              <div className="setting-item">
                <div className="setting-info">
                  <h4>{t('settings.externalBookingTitle', 'Your public booking page')}</h4>
                  <p>{t('settings.externalBookingHint', 'Customize the page, publish your link, and let customers reserve from any browser.')}</p>
                </div>
                <button className="btn btn-primary" onClick={() => { window.location.hash = '#/booking-link'; }}>
                  {t('settings.configureBookingLink', 'Configure booking link')}
                  <ArrowRight size={17} />
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Appointment / worker / schedule management (self-serve AI only).
            Bot Settings now opens from the button in the Bot card above. */}
        {!loadingStoreInfo && isAutoCreate && (
          <>
            {false && (<>
            <section className="settings-section card ai-section">
              <div className="section-header">
                <Bot size={20} className="section-icon" />
                <h2 className="section-title">{t('settings.aiBotSettings')}</h2>
              </div>
              <div className="section-content">
                {loadingPrompts ? (
                  <div className="loading-state">
                    <Loader2 className="spinner" size={24} />
                    <span>{t('settings.loadingAiSettings')}</span>
                  </div>
                ) : (
                  <>
                    {/* Business Info */}
                    <div className="settings-group">
                      <h3 className="group-title">{t('settings.businessInfo')}</h3>
                      
                      <div className="setting-item vertical">
                        <label className="setting-label">{t('settings.businessName')}</label>
                        <input
                          type="text"
                          className="input"
                          placeholder={t('settings.businessNamePlaceholder')}
                          value={prompts.business_name}
                          onChange={(e) => handlePromptChange('business_name', e.target.value)}
                        />
                        <span className="setting-hint">{t('settings.businessNameHint')}</span>
                      </div>

                      <div className="setting-item vertical">
                        <label className="setting-label">{t('settings.businessType')}</label>
                        <input
                          type="text"
                          className="input"
                          placeholder={t('settings.businessTypePlaceholder')}
                          value={prompts.business_type}
                          onChange={(e) => handlePromptChange('business_type', e.target.value)}
                        />
                        <span className="setting-hint">{t('settings.businessTypeHint')}</span>
                      </div>
                    </div>

                    {/* Bot Personality */}
                    <div className="settings-group">
                      <h3 className="group-title">{t('settings.botPersonality')}</h3>
                      
                      <div className="setting-item vertical">
                        <label className="setting-label">{t('settings.identityPrompt')}</label>
                        <textarea
                          className="input textarea"
                          rows={4}
                          placeholder={t('settings.identityPromptPlaceholder')}
                          value={prompts.identity_prompt}
                          onChange={(e) => handlePromptChange('identity_prompt', e.target.value)}
                        />
                        <span className="setting-hint">{t('settings.identityPromptHint')}</span>
                      </div>

                      <div className="setting-item vertical">
                        <label className="setting-label">{t('settings.openingMessage')}</label>
                        <textarea
                          className="input textarea"
                          rows={3}
                          placeholder={t('settings.openingMessagePlaceholder')}
                          value={prompts.opening_message}
                          onChange={(e) => handlePromptChange('opening_message', e.target.value)}
                        />
                        <span className="setting-hint">{t('settings.openingMessageHint')}</span>
                      </div>
                    </div>

                    {/* Custom Rules */}
                    <div className="settings-group">
                      <h3 className="group-title">{t('settings.customRules')}</h3>
                      
                      <div className="setting-item vertical">
                        <label className="setting-label">{t('settings.customInstructions')}</label>
                        <textarea
                          className="input textarea"
                          rows={5}
                          placeholder={t('settings.customInstructionsPlaceholder')}
                          value={prompts.custom_rules}
                          onChange={(e) => handlePromptChange('custom_rules', e.target.value)}
                        />
                        <span className="setting-hint">{t('settings.customInstructionsHint')}</span>
                      </div>
                    </div>

                    {/* Save Button */}
                    <div className="save-section">
                      <button 
                        className={`btn btn-primary save-btn ${savingPrompts ? 'loading' : ''}`}
                        onClick={savePrompts}
                        disabled={savingPrompts}
                      >
                        {savingPrompts ? (
                          <>
                            <Loader2 className="spinner" size={18} />
                            {t('settings.saving')}
                          </>
                        ) : (
                          <>
                            <Save size={18} />
                            {t('settings.savePrompts')}
                          </>
                        )}
                      </button>
                      {promptsSaveStatus === 'success' && (
                        <span className="save-status success">
                          <CheckCircle size={16} />
                          {t('settings.savedSuccessfully')}
                        </span>
                      )}
                      {promptsSaveStatus === 'error' && (
                        <span className="save-status error">
                          <AlertCircle size={16} />
                          {t('settings.failedToSave')}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </section>

            {/* Bot Configuration Section */}
            <section className="settings-section card">
              <div className="section-header">
                <Globe size={20} className="section-icon" />
                <h2 className="section-title">{t('settings.botConfiguration')}</h2>
              </div>
              <div className="section-content">
                {/* Capability toggles: gate whether the AI's prompt includes the
                    appointment-booking and product-catalog systems. */}
                <div className="setting-item">
                  <div className="setting-info">
                    <h4>{t('settings.aiAppointments', 'AI books appointments')}</h4>
                    <p>{t('settings.aiAppointmentsHint', 'Let the AI check availability and book/change appointments in chat.')}</p>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={botConfig.ai_appointments_enabled}
                      onChange={(e) => handleBotConfigChange('ai_appointments_enabled', e.target.checked)}
                    />
                    <span className="switch-slider"></span>
                  </label>
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <h4>{t('settings.aiProducts', 'AI shows products')}</h4>
                    <p>{t('settings.aiProductsHint', 'Let the AI present your product catalog and product details in chat.')}</p>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={botConfig.ai_products_enabled}
                      onChange={(e) => handleBotConfigChange('ai_products_enabled', e.target.checked)}
                    />
                    <span className="switch-slider"></span>
                  </label>
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <h4>{t('settings.botLanguage')}</h4>
                    <p>{t('settings.botLanguageHint')}</p>
                  </div>
                  <select
                    className="input setting-select"
                    value={botConfig.language}
                    onChange={(e) => handleBotConfigChange('language', e.target.value)}
                  >
                    {botLanguageOptions.map((lang) => (
                      <option key={lang.value} value={lang.value}>
                        {lang.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <h4>{t('settings.maxFutureAppointments')}</h4>
                    <p>{t('settings.maxFutureAppointmentsHint')}</p>
                  </div>
                  <select
                    className="input setting-select"
                    value={botConfig.max_future_appointments}
                    onChange={(e) => handleBotConfigChange('max_future_appointments', parseInt(e.target.value))}
                  >
                    {[1, 2, 3, 4, 5, 10].map((num) => (
                      <option key={num} value={num}>
                        {num}
                      </option>
                    ))}
                  </select>
                </div>

                {/* WhatsApp chat AI summary */}
                <div className="setting-item">
                  <div className="setting-info">
                    <h4>{t('settings.leadSummary', 'WhatsApp chat summary')}</h4>
                    <p>{t('settings.leadSummaryHint', 'Automatically keep an AI summary of each lead\'s WhatsApp chat, shown in the Leads workspace.')}</p>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={botConfig.lead_summary_enabled}
                      onChange={(e) => handleBotConfigChange('lead_summary_enabled', e.target.checked)}
                    />
                    <span className="switch-slider"></span>
                  </label>
                </div>

                {botConfig.lead_summary_enabled && (
                  <div className="setting-item">
                    <div className="setting-info">
                      <h4>{t('settings.leadSummaryLanguage', 'Summary language')}</h4>
                      <p>{t('settings.leadSummaryLanguageHint', 'The language the AI writes the chat summaries in.')}</p>
                    </div>
                    <select
                      className="input setting-select"
                      value={botConfig.lead_summary_language}
                      onChange={(e) => handleBotConfigChange('lead_summary_language', e.target.value)}
                    >
                      {botLanguageOptions.map((lang) => (
                        <option key={lang.value} value={lang.value}>
                          {lang.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Save Button */}
                <div className="save-section">
                  <button 
                    className={`btn btn-primary save-btn ${savingBotConfig ? 'loading' : ''}`}
                    onClick={saveBotConfig}
                    disabled={savingBotConfig}
                  >
                    {savingBotConfig ? (
                      <>
                        <Loader2 className="spinner" size={18} />
                        {t('settings.saving')}
                      </>
                    ) : (
                      <>
                        <Save size={18} />
                        {t('settings.saveConfiguration')}
                      </>
                    )}
                  </button>
                  {botConfigSaveStatus === 'success' && (
                    <span className="save-status success">
                      <CheckCircle size={16} />
                      {t('settings.savedSuccessfully')}
                    </span>
                  )}
                  {botConfigSaveStatus === 'error' && (
                    <span className="save-status error">
                      <AlertCircle size={16} />
                      {t('settings.failedToSave')}
                    </span>
                  )}
                </div>
              </div>
            </section>
            </>)}

            <div className="settings-group-divider">
              {t('settings.appointmentSettings', 'Appointment settings')}
            </div>

            {/* Appointment Types Section */}
            <section className="settings-section card">
              <div className="section-header">
                <Calendar size={20} className="section-icon" />
                <h2 className="section-title">{t('settings.appointmentTypes')}</h2>
                <button className="btn btn-primary btn-sm" onClick={() => openTypeModal()}>
                  <Plus size={16} />
                  {t('common.add')}
                </button>
              </div>
              <div className="section-content">
                {loadingTypes ? (
                  <div className="loading-state">
                    <Loader2 className="spinner" size={24} />
                  </div>
                ) : appointmentTypes.length === 0 ? (
                  <p className="empty-text">{t('settings.noTypes')}</p>
                ) : (
                  <div className="items-list">
                    {appointmentTypes.map((type) => (
                      <div key={type.id} className="list-item">
                        <div className="item-info">
                          <h4>{type.name}</h4>
                          <p>{type.duration_minutes} {t('common.minutes')} {type.price ? `• ${type.price}` : ''}</p>
                        </div>
                        <div className="item-actions">
                          <button className="btn-icon" onClick={() => openTypeModal(type)}>
                            <Edit2 size={16} />
                          </button>
                          <button className="btn-icon danger" onClick={() => deleteType(type.id)}>
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* Workers Section */}
            <section className="settings-section card">
              <div className="section-header">
                <Users size={20} className="section-icon" />
                <h2 className="section-title">{t('settings.workers')}</h2>
                <button className="btn btn-primary btn-sm" onClick={() => openWorkerModal()}>
                  <Plus size={16} />
                  {t('common.add')}
                </button>
              </div>
              <div className="section-content">
                {loadingWorkers ? (
                  <div className="loading-state">
                    <Loader2 className="spinner" size={24} />
                  </div>
                ) : workers.length === 0 ? (
                  <p className="empty-text">{t('settings.noWorkers')}</p>
                ) : (
                  <div className="items-list">
                    {workers.map((worker) => (
                      <div key={worker.id} className="list-item">
                        <div className="item-info">
                          <h4>{worker.name}</h4>
                          <p>{worker.is_active ? t('common.active') : t('common.inactive')}</p>
                        </div>
                        <div className="item-actions">
                          <button className="btn-icon" onClick={() => openWorkerModal(worker)}>
                            <Edit2 size={16} />
                          </button>
                          <button className="btn-icon danger" onClick={() => deleteWorker(worker.id)}>
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* Schedules Section */}
            <section className="settings-section card">
              <div className="section-header">
                <Clock size={20} className="section-icon" />
                <h2 className="section-title">{t('settings.schedules')}</h2>
                <button className="btn btn-primary btn-sm" onClick={() => openScheduleModal()}>
                  <Plus size={16} />
                  {t('common.add')}
                </button>
              </div>
              <div className="section-content">
                {loadingSchedules ? (
                  <div className="loading-state">
                    <Loader2 className="spinner" size={24} />
                  </div>
                ) : schedules.length === 0 ? (
                  <p className="empty-text">{t('settings.noSchedules')}</p>
                ) : (
                  <div className="items-list">
                    {schedules.map((schedule) => (
                      <div key={schedule.id} className="list-item">
                        <div className="item-info">
                          <h4>{schedule.day_name || dayOptions.find(d => d.value === schedule.day_of_week)?.label}</h4>
                          <p>{schedule.start_time} - {schedule.end_time} {schedule.worker_name ? `• ${schedule.worker_name}` : ''}</p>
                        </div>
                        <div className="item-actions">
                          <button className="btn-icon" onClick={() => openScheduleModal(schedule)}>
                            <Edit2 size={16} />
                          </button>
                          <button className="btn-icon danger" onClick={() => deleteSchedule(schedule.id)}>
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </>
        )}

        <div className="settings-group-divider">
          {t('settings.accountSettings', 'Account settings')}
        </div>

        {/* Appearance Section */}
        <section className="settings-section card">
          <div className="section-header">
            <Palette size={20} className="section-icon" />
            <h2 className="section-title">{t('settings.appearance', 'Appearance')}</h2>
          </div>
          <div className="section-content">
            <div className="setting-item">
              <div className="setting-info">
                <h4>{t('settings.theme', 'Theme')}</h4>
                <p>{t('settings.themeDescription', 'Choose light or dark mode.')}</p>
              </div>
              <select
                className="input setting-select"
                value={theme}
                onChange={(e) => changeTheme(e.target.value)}
              >
                <option value="dark">{t('settings.themeDark', 'Dark')}</option>
                <option value="light">{t('settings.themeLight', 'Light')}</option>
                <option value="system">{t('settings.themeSystem', 'System')}</option>
              </select>
            </div>
          </div>
        </section>

        {/* Language Section */}
        <section className="settings-section card">
          <div className="section-header">
            <Globe size={20} className="section-icon" />
            <h2 className="section-title">{t('settings.language')}</h2>
          </div>
          <div className="section-content">
            <div className="setting-item">
              <div className="setting-info">
                <h4>{t('settings.selectLanguage')}</h4>
                <p>{t('settings.themeDescription')}</p>
              </div>
              <select
                className="input setting-select"
                value={currentLanguage}
                onChange={handleLanguageChange}
              >
                {availableLanguages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.nativeName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>

        {/* Account Section */}
        <section className="settings-section card span-full">
          <div className="section-header">
            <User size={20} className="section-icon" />
            <h2 className="section-title">{t('settings.account')}</h2>
          </div>
          <div className="section-content">
            {/* Profile Picture */}
            <div className="setting-item profile-picture-section">
              <div className="setting-info">
                <h4>{t('settings.profilePicture')}</h4>
                <p>{t('settings.profilePictureHint')}</p>
              </div>
              <div className="profile-picture-upload">
                <div className="profile-picture-preview">
                  {user?.profile_picture_url ? (
                    <img
                      src={user.profile_picture_url}
                      alt="Profile"
                      className="profile-pic-img"
                    />
                  ) : (
                    <div className="profile-pic-placeholder">
                      <User size={32} />
                    </div>
                  )}
                  <button
                    className="profile-pic-overlay"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPicture}
                  >
                    {uploadingPicture ? (
                      <Loader2 className="spinner" size={20} />
                    ) : (
                      <Camera size={20} />
                    )}
                  </button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  className="hidden-file-input"
                  onChange={handleProfilePictureUpload}
                />
                <button
                  className="btn btn-secondary btn-sm upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPicture}
                >
                  {uploadingPicture ? (
                    <>
                      <Loader2 className="spinner" size={14} />
                      {t('settings.uploading')}
                    </>
                  ) : (
                    <>
                      <Upload size={14} />
                      {t('settings.uploadPicture')}
                    </>
                  )}
                </button>
                {pictureSuccess && (
                  <span className="save-status success">
                    <CheckCircle size={14} />
                    {t('settings.profilePicUploaded')}
                  </span>
                )}
                {pictureError && (
                  <span className="save-status error">
                    <AlertCircle size={14} />
                    {pictureError}
                  </span>
                )}
              </div>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <h4>{t('common.email')}</h4>
                <p>{user?.email || t('common.unknown')}</p>
              </div>
            </div>
            {storeInfo && (
              <div className="setting-item">
                <div className="setting-info">
                  <h4>{t('settings.storeId')}</h4>
                  <p>{storeInfo.store_id}</p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Notifications Section (Coming Soon) */}
        <section className="settings-section card disabled">
          <div className="section-header">
            <Bell size={20} className="section-icon" />
            <h2 className="section-title">{t('settings.notifications')}</h2>
            <span className="badge badge-info">{t('settings.comingSoon')}</span>
          </div>
          <div className="section-content">
            <p className="coming-soon-text">{t('settings.notificationsComingSoon')}</p>
          </div>
        </section>

        {/* About Section */}
        <section className="settings-section card">
          <div className="section-header">
            <Info size={20} className="section-icon" />
            <h2 className="section-title">{t('settings.about')}</h2>
          </div>
          <div className="section-content">
            <div className="setting-item">
              <div className="setting-info">
                <h4>{t('settings.version')}</h4>
                <p>1.0.0</p>
              </div>
            </div>
            <div className="setting-item">
              <div className="setting-info">
                <h4>{t('settings.rodBotControlPanel')}</h4>
                <p>{t('settings.rodBotDescription')}</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Worker Modal */}
      {showWorkerModal && (
        <div className="modal-overlay" onClick={() => setShowWorkerModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingWorker ? t('workers.editWorker') : t('workers.addWorker')}</h3>
              <button className="btn-icon" onClick={() => setShowWorkerModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('common.name')}</label>
                <input
                  type="text"
                  className="input"
                  placeholder={t('settings.workerName')}
                  value={workerForm.name}
                  onChange={(e) => setWorkerForm({ ...workerForm, name: e.target.value })}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowWorkerModal(false)}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-primary" onClick={saveWorker}>
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="modal-overlay" onClick={() => setShowScheduleModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingSchedule ? t('schedules.editSchedule') : t('schedules.addSchedule')}</h3>
              <button className="btn-icon" onClick={() => setShowScheduleModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('settings.dayOfWeek')}</label>
                <select
                  className="input"
                  value={scheduleForm.day_of_week}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, day_of_week: parseInt(e.target.value) })}
                >
                  {dayOptions.map((day) => (
                    <option key={day.value} value={day.value}>{day.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>{t('settings.startTime')}</label>
                  <input
                    type="time"
                    className="input"
                    value={scheduleForm.start_time}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, start_time: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>{t('settings.endTime')}</label>
                  <input
                    type="time"
                    className="input"
                    value={scheduleForm.end_time}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, end_time: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>{t('settings.worker')}</label>
                <select
                  className="input"
                  value={scheduleForm.worker_id || ''}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, worker_id: e.target.value ? parseInt(e.target.value) : null })}
                >
                  <option value="">{t('settings.allWorkers')}</option>
                  {workers.map((worker) => (
                    <option key={worker.id} value={worker.id}>{worker.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowScheduleModal(false)}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-primary" onClick={saveSchedule}>
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Appointment Type Modal */}
      {showTypeModal && (
        <div className="modal-overlay" onClick={() => setShowTypeModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingType ? t('appointmentTypes.editType') : t('appointmentTypes.addType')}</h3>
              <button className="btn-icon" onClick={() => setShowTypeModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('common.name')}</label>
                <input
                  type="text"
                  className="input"
                  placeholder={t('settings.typeName')}
                  value={typeForm.name}
                  onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>{t('settings.duration')}</label>
                  <input
                    type="number"
                    className="input"
                    min="5"
                    step="5"
                    value={typeForm.duration_minutes}
                    onChange={(e) => setTypeForm({ ...typeForm, duration_minutes: parseInt(e.target.value) })}
                  />
                </div>
                <div className="form-group">
                  <label>{t('settings.price')} ({t('settings.priceOptional')})</label>
                  <input
                    type="number"
                    className="input"
                    min="0"
                    step="0.01"
                    value={typeForm.price}
                    onChange={(e) => setTypeForm({ ...typeForm, price: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>{t('settings.description')}</label>
                <textarea
                  className="input textarea"
                  rows={2}
                  placeholder={t('settings.descriptionPlaceholder')}
                  value={typeForm.description}
                  onChange={(e) => setTypeForm({ ...typeForm, description: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>{t('settings.promptNotes')}</label>
                <textarea
                  className="input textarea"
                  rows={2}
                  placeholder={t('settings.promptNotesPlaceholder')}
                  value={typeForm.prompt_notes}
                  onChange={(e) => setTypeForm({ ...typeForm, prompt_notes: e.target.value })}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowTypeModal(false)}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-primary" onClick={saveType}>
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bot Settings Modal (personality, rules, pictures, live preview) */}
      {showBotSettings && (
        <BotSettingsModal
          onClose={() => setShowBotSettings(false)}
          isRTL={isRTL}
          theme={theme}
          prompts={prompts}
          handlePromptChange={handlePromptChange}
          setPrompts={setPrompts}
          savePrompts={savePrompts}
          savingPrompts={savingPrompts}
          promptsSaveStatus={promptsSaveStatus}
          botConfig={botConfig}
          handleBotConfigChange={handleBotConfigChange}
          saveBotConfig={saveBotConfig}
          savingBotConfig={savingBotConfig}
          botConfigSaveStatus={botConfigSaveStatus}
          botLanguageOptions={botLanguageOptions}
        />
      )}
    </div>
  );
};

export default SettingsPage;
